import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';

// Animation keyframes
const slideInFromBottom = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInFromRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const rotateIn = keyframes`
  from {
    opacity: 0;
    transform: rotate(-10deg) scale(0.9);
  }
  to {
    opacity: 1;
    transform: rotate(0) scale(1);
  }
`;

const blurIn = keyframes`
  from {
    opacity: 0;
    filter: blur(10px);
  }
  to {
    opacity: 1;
    filter: blur(0);
  }
`;

const bounceIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.3);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  70% {
    opacity: 1;
    transform: scale(0.9);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const slideAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateY(60px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

// Animation types
export type AnimationType = 'slideInFromBottom' | 'slideInFromLeft' | 'slideInFromRight' | 'fadeIn' | 'scaleIn' | 'rotateIn' | 'blurIn' | 'bounceIn' | 'slideAndFade';

interface ScrollAnimationProps {
  children: React.ReactNode;
  animation?: AnimationType;
  delay?: number;
  duration?: number;
  threshold?: number;
  triggerOnce?: boolean;
  className?: string;
}

const getAnimation = (type: AnimationType) => {
  switch (type) {
    case 'slideInFromLeft':
      return slideInFromLeft;
    case 'slideInFromRight':
      return slideInFromRight;
    case 'fadeIn':
      return fadeIn;
    case 'scaleIn':
      return scaleIn;
    case 'rotateIn':
      return rotateIn;
    case 'blurIn':
      return blurIn;
    case 'bounceIn':
      return bounceIn;
    case 'slideAndFade':
      return slideAndFade;
    default:
      return slideInFromBottom;
  }
};

const AnimatedContainer = styled.div<{
  $isVisible: boolean;
  $animation: AnimationType;
  $delay: number;
  $duration: number;
}>`
  /* Start visible as fallback, animation will handle the fade-in */
  opacity: ${props => props.$isVisible ? 1 : 0.99};
  
  ${props => props.$isVisible && css`
    animation: ${getAnimation(props.$animation)} ${props.$duration}ms ease-out ${props.$delay}ms forwards;
  `}
  
  /* Fallback for when animations are disabled or broken */
  @media (prefers-reduced-motion: reduce) {
    opacity: 1 !important;
    animation: none !important;
  }
`;

export const ScrollAnimation: React.FC<ScrollAnimationProps> = ({
  children,
  animation = 'slideInFromBottom',
  delay = 0,
  duration = 600,
  threshold = 0.1,
  triggerOnce = true,
  className
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(entry.target);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold, triggerOnce]);

  return (
    <AnimatedContainer
      ref={ref}
      $isVisible={isVisible}
      $animation={animation}
      $delay={delay}
      $duration={duration}
      className={className}
    >
      {children}
    </AnimatedContainer>
  );
};

// Hook for manual scroll animation control
export const useScrollAnimation = (
  threshold: number = 0.1,
  triggerOnce: boolean = true
) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(entry.target);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold, triggerOnce]);

  return { ref, isVisible };
};

export default ScrollAnimation;